import { useRef, useState, useEffect, useCallback } from 'preact/hooks';
import { tryFocus } from '@/utils';
import { useWcagKeyboardEvents } from '@/features/conversational';
import { HandleTabIndex } from '@/features/elements';
import { useConfigStore, useFormProps } from '../stores/ConfigStore';
import { useFormStore } from '../stores/FormStore';
import useJsEvents from '../hooks/useJsEvents';
import SvgIcon from './SvgIcon';

export default function LanguageToggle() {
	const { customDomain } = useFormProps();
	const { formType, contentDivRef } = useFormStore();
	const {
		translationSet,
		properties: { translation },
		actions
	} = useConfigStore();
	const triggerEvent = useJsEvents();

	const toggleRef = useRef(null);
	const closeCrossRef = useRef(null);
	const [open, setOpen] = useState(false);

	const toggleList = useCallback(
		f => setOpen(prev => (typeof f === 'undefined' ? !prev : f)),
		[]
	);

	const setOption = value => {
		actions.setLanguage(value);
		toggleList(false);
		triggerEvent('language_changed');
	};

	const { onKeyDown } = useWcagKeyboardEvents({
		onSpaceEnter: (e, props) => {
			if (!open) {
				e.preventDefault();
				toggleList();
				tryFocus(closeCrossRef.current);
			}
		},
		onTabUp: (e, props) => {
			if (!props) {
				e.preventDefault();
				toggleList();
			}
			if (props?.isFirstOption) {
				e.preventDefault();
				tryFocus(closeCrossRef.current);
			}
		},
		onTabDown: (e, props) => {
			if (props?.isLastOption) {
				e.preventDefault();
				tryFocus(closeCrossRef.current);
			}
		}
	});

	useEffect(() => {
		const toggleListOuter = e => {
			if (!toggleRef.current?.contains(e.target)) {
				toggleList(false);
			}
		};

		const clickAwayElement = formType === 'embed' ? document : contentDivRef.current;

		setTimeout(() => {
			if (!open) {
				clickAwayElement?.removeEventListener('click', toggleListOuter);
			} else {
				clickAwayElement?.addEventListener('click', toggleListOuter);
			}
		});

		return () => {
			clickAwayElement?.removeEventListener('click', toggleListOuter);
		};
	}, [open, formType, contentDivRef, toggleList]);

	const defaultArray = [{ value: 'default', label: 'Default language', countrycode: '' }];
	const optionArray = defaultArray.concat(
		Object.keys(translation.toggleOptions).map(
			optionKey => translation.toggleOptions[optionKey]
		)
	);

	const currentLanguage = optionArray.filter(
		option => option.value === translationSet
	)[0];

	const flagUrlBase = `https://${customDomain}/assets/img/countryflags`;

	return (
		<div class={'language-toggle-wrap wcag'}>
			<div
				className={`language-toggle ${open ? 'open' : ''} ${
					currentLanguage?.countrycode ? 'has-icon' : ''
				}`}
			>
				<HandleTabIndex
					component="button"
					type="button"
					onClick={() => {
						toggleList();
					}}
					onKeyDown={e => onKeyDown(e)}
					ref={toggleRef}
					aria-label="choose language"
					className="toggle"
				>
					<SvgIcon
						icon="language"
						alt="language select icon"
					/>
					<span className="current-flag">
						{currentLanguage?.countrycode ? (
							<img
								src={`${flagUrlBase}/${currentLanguage.countrycode}.svg`}
								alt={`${currentLanguage.label} flag`}
							/>
						) : (
							''
						)}
					</span>
					<span className="current">
						{currentLanguage?.value === 'default' ? '' : currentLanguage?.label}
					</span>
					<span className="caret" />
				</HandleTabIndex>

				<div className="toggle-menu">
					<div className="toggle-caret" />
					<div className="toggle-item-wrap">
						<span className="toggle-menu-close">
							<HandleTabIndex
								component="button"
								type="button"
								onClick={() => toggleList()}
								ref={closeCrossRef}
								onKeyDown={e => onKeyDown(e)}
								aria-label="close language selection"
								className="toggle-close"
							/>
						</span>
						{optionArray.map((option, index) => {
							return (
								<HandleTabIndex
									component="button"
									type="button"
									onClick={() => setOption(option.value)}
									onKeyDown={e => {
										onKeyDown(e, {
											value: option.value,
											isFirstOption: index === 0,
											isLastOption: index === optionArray.length - 1
										});
									}}
									className={`toggle-menu-item ${
										translationSet === option.value ||
										(!translationSet && option.value === 'default')
											? 'selected'
											: ''
									}`}
								>
									<span className="toggle-menu-flag">
										{option.countrycode ? (
											<img
												src={`${flagUrlBase}/${option.countrycode}.svg`}
												alt={`${option.label} flag`}
											/>
										) : (
											''
										)}{' '}
									</span>
									<span className="toggle-menu-label">
										{!option.countrycode ? (
											<span style={{ fontStyle: 'italic' }}>{option.label}</span>
										) : (
											option.label
										)}
									</span>
								</HandleTabIndex>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
}
